import { useId, useState, type Ref } from "react";
import { Link } from "react-router";

import { useProjectOverviewQuery } from "@/api/hooks";
import { usePreviewCandidatesQuery } from "@/api/preview";
import { useTenantVersionsQuery } from "@/api/tenantRuntime";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { isCurrent } from "@/lib/candidateFreshness";
import { labCandidateLabel, sortCandidatesNewestFirst } from "@/lib/labModel";
import { labBackPath } from "@/lib/labUrl";
import {
  readThemePreference,
  setThemePreference,
  type ThemePreference,
} from "@/lib/theme";
import { formatVersionsChip } from "@/lib/tenantVersions";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Select } from "@/ui/Input";
import { DefaultNote } from "@/ui/project/DefaultNote";

import { LabTopBarRun } from "./LabExportControls";

/** The Lab's slim top bar: mark, project, tenant, testing version, way back. */
export function LabTopBar({
  projectId,
  candidateId,
  onCandidateChange,
  headingRef,
  compareOn = false,
  compareHasSecond = false,
  onCompareToggle,
}: {
  projectId: string;
  candidateId: string | null;
  onCandidateChange: (candidateId: string) => void;
  /** Focus target after an in-app navigation into the Lab. */
  headingRef?: Ref<HTMLHeadingElement>;
  /** Compare mode: the centre splits into two runs. Omit `onCompareToggle` to hide the control. */
  compareOn?: boolean;
  compareHasSecond?: boolean;
  onCompareToggle?: () => void;
}) {
  const selectId = useId();
  const themeId = useId();
  const [theme, setTheme] = useState(readThemePreference);
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const items = sortCandidatesNewestFirst(candidates.data?.items ?? []);
  const project = overview.data?.project;
  const versions = useTenantVersionsQuery(
    project?.previewTenantId,
    useRuntimeConfig().features.tenantTools,
  );

  return (
    <header className="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-card px-4 py-2">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground"
        >
          L
        </span>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-base font-semibold focus:outline-none"
        >
          Reliv Labs
        </h1>
      </div>

      {project ? (
        <span
          dir="auto"
          className="min-w-0 max-w-[24ch] truncate text-sm font-medium"
        >
          {project.name}
        </span>
      ) : null}
      {project ? (
        <Badge tone="outline" dir="ltr">
          {project.previewTenantId}
        </Badge>
      ) : null}
      {versions.data ? (
        <Badge tone="outline" dir="ltr">
          {formatVersionsChip(versions.data)}
        </Badge>
      ) : null}
      <DefaultNote
        projectId={projectId}
        candidateId={candidateId}
        compact
      />

      <LabTopBarRun projectId={projectId} />

      {onCompareToggle !== undefined ? (
        <div className="flex items-center gap-2">
          <Button
            variant={compareOn ? "primary" : "outline"}
            size="sm"
            aria-pressed={compareOn}
            onClick={onCompareToggle}
          >
            Compare
          </Button>
          {compareOn && !compareHasSecond ? (
            <span role="status" className="text-xs text-muted-foreground">
              Pick the second run in History
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 sm:ms-auto">
        <label htmlFor={selectId} className="text-sm text-muted-foreground">
          Test version
        </label>
        <Select
          id={selectId}
          className="h-8 w-40"
          value={candidateId ?? ""}
          disabled={items.length === 0}
          onChange={(event) => onCandidateChange(event.target.value)}
        >
          {items.length === 0 ? (
            <option value="">
              {candidates.isPending
                ? "Loading testing versions…"
                : "No testing versions yet"}
            </option>
          ) : (
            items.map((candidate) => (
              <option key={candidate.candidateId} value={candidate.candidateId}>
                {labCandidateLabel(candidate)}
                {candidate.validity?.state === "expired"
                  ? " (expired)"
                  : !isCurrent(candidate)
                    ? " (not current)"
                    : ""}
              </option>
            ))
          )}
        </Select>
        <label htmlFor={themeId} className="sr-only">
          Theme
        </label>
        <Select
          id={themeId}
          title="Theme"
          className="h-8 w-auto"
          value={theme}
          onChange={(event) => {
            const next = event.target.value as ThemePreference;
            setTheme(next);
            setThemePreference(next);
          }}
        >
          <option value="system">System</option>
          <option value="dark">Dark</option>
          <option value="light">Light</option>
        </Select>
        <Button variant="outline" size="sm" asChild>
          <Link to={labBackPath(projectId)}>Back to project</Link>
        </Button>
      </div>
    </header>
  );
}
